import { useCurrentNewsRibbon } from '@/context/NewsRibbon';
import { MuiContainer, MuiIconButton } from '@/features/shared/material-ui';
import { CaptureClicksRichText } from '@/features/shared/RichText';
import { Close } from '@material-ui/icons';
import clsx from 'clsx';
import React, { useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { DataLayerImpressionPopUp } from '../DataLayerImpressionPopUp';
import { NewsRibbonProps } from '../NewsRibbon.interface';
import { useBannerResize } from '../useBannerResize';
import useStyles from './SimpleRibbon.styles';

const dataLayerPosition = 'news_ribbon';

const SimpleRibbon = (props: Br.PropsWithContentButtons<NewsRibbonProps>) => {
  const classes = useStyles({});
  const location = useLocation();
  const {
    backgroundColor,
    detailContent,
    bodyBanner,
    className,
    collapsible,
    contentButtons,
    theme,
    datalayerVariant,
  } = props;
  const title = props?.parentTitle || props?.titleValue || '';
  const contentRef = React.useRef<HTMLDivElement>(null);
  const { close, closed } = useCurrentNewsRibbon();
  const { height } = useBannerResize({
    enabled: true,
    ref: contentRef,
    selector: `.${classes.innerContent}`,
  });
  const content = useMemo(() => detailContent || bodyBanner, [detailContent, bodyBanner]);

  const handleClose = useCallback(() => {
    close(250);
  }, [close]);

  const rootClassName = clsx(
    classes.root,
    `${theme}-theme`,
    `bg-color-${backgroundColor}`,
    className,
    'news-ribbon',
  );

  const rtcClassName = clsx(
    classes.rtc,
    `${theme}-theme`,
    collapsible && 'is-collapsible',
    'gtm-news-ribbon-click',
  );

  const closeIconClassName = clsx(classes.closeIcon, `${theme}-theme`, 'gtm-news-ribbon-close');

  return (
    <>
      <section
        data-testid="qa-news-ribbon-component"
        className={rootClassName}
        data-datalayer-position={dataLayerPosition}
        data-datalayer-variant={datalayerVariant}
        data-datalayer-title={title}
        style={{ height, marginTop: closed ? -height : 0 }}
      >
        <MuiContainer className={classes.container} ref={contentRef}>
          {contentButtons?.content}
          <div className={classes.innerContent}>
            <div className={classes.columnContent}>
              {content && (
                <CaptureClicksRichText
                  customStyles
                  data-testid="qa-news-ribbon-component-text"
                  content={content}
                  className={rtcClassName}
                  theme={theme}
                />
              )}
            </div>
          </div>
        </MuiContainer>
        {collapsible && (
          <MuiIconButton
            aria-label="close"
            className={closeIconClassName}
            data-testid="close--button"
            size="small"
            onClick={handleClose}
          >
            <Close fontSize="small" color="inherit" />
          </MuiIconButton>
        )}
      </section>
      <DataLayerImpressionPopUp
        title={title}
        position={dataLayerPosition}
        {...(props?.isGlobalNewsRibbon && { key: location.pathname })}
      />
    </>
  );
};

export default React.memo(SimpleRibbon);
